/**
 * Aufbereitung des Dankestextes für die Anzeige nach dem Absenden.
 *
 * Der Ersteller darf HTML oder Markdown hinterlegen. HTML wird vor der
 * Ausgabe auf eine sichere Teilmenge reduziert (keine Skripte, keine
 * Event-Handler, Links nur mit http(s)/mailto), Markdown wird unterstützt.
 */

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'UL', 'OL', 'LI',
  'H1', 'H2', 'H3', 'H4', 'H5', 'BLOCKQUOTE', 'CODE', 'PRE', 'A', 'SPAN', 'DIV', 'HR',
]);

const BLOCKED_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'FORM',
  'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'OPTION', 'SVG', 'MATH', 'IMG', 'VIDEO', 'AUDIO',
]);

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function isSafeHref(href: string): boolean {
  const value = href.trim();
  if (!value) return false;
  if (value.startsWith('/') || value.startsWith('#')) return true;
  try {
    const parsed = new URL(value, window.location.origin);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'mailto:';
  } catch {
    return false;
  }
}

function sanitizeInto(source: Node, target: HTMLElement): void {
  source.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(document.createTextNode(node.textContent ?? ''));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toUpperCase();
    if (BLOCKED_TAGS.has(tag)) return;

    // Unbekannte Tags entfernen, aber ihren Inhalt behalten
    if (!ALLOWED_TAGS.has(tag)) {
      sanitizeInto(element, target);
      return;
    }

    const clean = document.createElement(tag.toLowerCase());
    if (tag === 'A') {
      const href = element.getAttribute('href') ?? '';
      if (isSafeHref(href)) {
        clean.setAttribute('href', href.trim());
        clean.setAttribute('target', '_blank');
        clean.setAttribute('rel', 'noopener noreferrer');
      }
    }
    sanitizeInto(element, clean);
    target.appendChild(clean);
  });
}

function sanitizeHtml(value: string): string {
  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const container = document.createElement('div');
  sanitizeInto(parsed.body, container);
  return container.innerHTML;
}

function renderMarkdown(value: string): string {
  const inline = (text: string) =>
    text
      .replace(
        /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
      )
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
      .replace(/`([^`]+)`/g, '<code>$1</code>');

  const html: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  const closeList = () => {
    if (listType) {
      html.push(`</${listType}>`);
      listType = null;
    }
  };

  for (const line of escapeHtml(value).split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) {
      closeList();
      html.push('<br/>');
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      closeList();
      const level = Math.min(heading[1].length + 1, 5);
      html.push(`<h${level}>${inline(heading[2])}</h${level}>`);
      continue;
    }

    const unordered = trimmed.match(/^[-*]\s+(.*)$/);
    if (unordered) {
      if (listType !== 'ul') {
        closeList();
        html.push('<ul>');
        listType = 'ul';
      }
      html.push(`<li>${inline(unordered[1])}</li>`);
      continue;
    }

    const ordered = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (ordered) {
      if (listType !== 'ol') {
        closeList();
        html.push('<ol>');
        listType = 'ol';
      }
      html.push(`<li>${inline(ordered[1])}</li>`);
      continue;
    }

    closeList();
    html.push(`<p>${inline(trimmed)}</p>`);
  }

  closeList();
  return html.join('');
}

/** Liefert sicheres HTML für die Anzeige des Dankestextes. */
export function renderRichText(input?: string | null): string {
  if (!input) return '';
  const value = input.trim();
  if (!value) return '';
  if (!/<\/?[a-z][\s\S]*>/i.test(value)) return renderMarkdown(value);
  return sanitizeHtml(value);
}

/** Prüft, ob eine konfigurierte Ziel-URL für eine Weiterleitung taugt. */
export function isHttpUrl(value?: string | null): boolean {
  if (!value) return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
